import moment from 'moment';
import { Link } from 'react-router-dom';
import Modal from '@mui/material/Modal';
import { useSelector, useDispatch } from "react-redux";
import React, { useEffect, useState, useRef } from "react";
import { TextField, Select, MenuItem, FormControl, Checkbox, ListItemText, OutlinedInput, Switch, Tooltip } from "@mui/material";

import 'react-circular-progressbar/dist/styles.css';
import { toggleModal } from "../../../reducers/modalReducer";
import Button from "../../../components/common/Button/index.jsx";
import { getRequest, postRequest, showMessage } from "../../../config";
import ModalWrapper from "../../../components/common/Modal/ModalWrapper.jsx";

export default function ShareDiligencePack({ company: propCompany, setCompany, getCompany, setTab }) {

    const dispatch = useDispatch();
    const user = useSelector(state => state.user);
    const modal = useSelector(state => state.modal);

    const { selectedCompany } = useSelector(state => state.company);
    const company = selectedCompany || propCompany;

    const permissionLists = ["Investment Readiness Report", "Market Opportunity Report", "Competitor Analysis Report", "Route to Market Report", "Technology Competitive Advantage Report",
        "SWOT Report", "Data Repository", company?.["testType"] ? `Quick Business Audit ${company["testType"]}` : "Quick Business Audit", "Warranty Disclosure", "Directors' Questionnaires"]

    let [allUsers, setAllUsers] = useState([]);
    let [docsArray, setDocsArray] = useState([]);
    let [selectedUser, setSelectedUser] = useState({});
    let [filePermissions, setFilePermissions] = useState([]); // Changed from object to array
    let [reportPermissions, setReportPermissions] = useState({});
    let [externalUserDetails, setExternalUserDetails] = useState({ firstName: '', lastName: '', email: '' });

    useEffect(() => {
        if (!company) return;

        const users = [];
        if (company?.['externalMembers'] && Object.keys(company['externalMembers']).length > 0) {
            Object.entries(company['externalMembers']).forEach(([memberKey, externalMember]) => {
                users.push({
                    id: memberKey,
                    email: externalMember['email'],
                    type: externalMember?.['role'] == 'Guest' ? 'External' : externalMember['role'],
                    firm: externalMember?.['role'] == 'Guest' ? 'External User' : externalMember?.['cName'],
                    filePermissions: externalMember['filePermissions'] || [], // Changed to array
                    reportPermissions: externalMember['reportPermissions'] || {},
                    name: `${externalMember['firstName'] || ''} ${externalMember['lastName'] || ''}`.trim(),
                    lastLoginAt: (externalMember?.['lastLoginAt'] || externalMember?.['lastLogin']) ? moment(externalMember?.['lastLoginAt'] || externalMember['lastLogin']).format('lll') : 'Never',
                    status: externalMember['status'] || 'active',
                    shareCode: externalMember['shareCode'],
                });
            });
        }
        setAllUsers(users);

        if (docsArray.length == 0) getInvestmentModuleFiles(company['_id']);
    }, [company]);

    const getInvestmentModuleFiles = async (companyId) => {
        let res = await getRequest(`/company/getInvestmentModuleFiles/${companyId}`);
        if (res['code'] == 200) setDocsArray(res['body']['files']);
    };

    const updatePermissions = async (userId, permissionType, selectedItems) => {
        if (!company?.externalMembers?.[userId]) return showMessage('Member not found to update permissions');
        if (!['reportPermissions', 'filePermissions'].includes(permissionType)) return showMessage('Permission type is invalid');

        let permissions;
        if (permissionType === 'filePermissions') permissions = selectedItems || [];
        else {
            permissions = {};
            if (selectedItems && Array.isArray(selectedItems)) selectedItems.forEach(item => { permissions[item] = true });
        }

        try {
            const originalPermissions = company.externalMembers[userId][permissionType] || (permissionType === 'filePermissions' ? [] : {});
            let updatedCompany = { ...company, externalMembers: { ...company.externalMembers, [userId]: { ...company.externalMembers[userId], [permissionType]: permissions } } };
            dispatch(setCompany(updatedCompany));

            let data = { userId, action: 'updatePermissions', permissionType, permissions };
            let res = await postRequest(`/company/updateExternalUser/${company['_id']}`, data);
            if (res['code'] === 200) showMessage('Permissions updated successfully');
            else {
                let revertedCompany = { ...company, externalMembers: { ...company.externalMembers, [userId]: { ...company.externalMembers[userId], [permissionType]: originalPermissions } } };
                dispatch(setCompany(revertedCompany));
                showMessage(res['message'] || 'Failed to update permissions');
            }
        } catch (error) {
            console.error('Error updating permissions:', error);
            showMessage('Error updating permissions');
        }
    };

    const handleResendInvite = async (userId, email) => {
        try {
            const data = { userId, email };
            const res = await postRequest(`/company/resendInvite/${company['_id']}`, data);
            if (res['code'] === 200) {
                showMessage(`Invite resent successfully to ${email}`);
                await getCompany();
            }
            else showMessage(res['message'] || 'Failed to resend invite');
        } catch (error) {
            console.error('Error resending invite:', error);
            showMessage('Error resending invite');
        }
    };

    const handleCopyShareLink = (shareCode) => {
        if (!shareCode) return showMessage('Share link not available');
        const shareLink = `${window.location.origin}/shareCode/${shareCode}/diligence-pack/${company['_id']}`;
        navigator.clipboard.writeText(shareLink)
            .then(() => showMessage('Share link copied to clipboard'))
            .catch(() => showMessage('Failed to copy link'));
    };

    const handleDeleteUser = async (userId) => {
        try {
            if (company.externalMembers && company.externalMembers[userId]) {
                let updateCompany = { ...company, externalMembers: { ...company.externalMembers } };
                delete updateCompany.externalMembers[userId];
                dispatch(setCompany(updateCompany));
            }
            const res = await postRequest(`/company/removeExternalUser/${company['_id']}`, { userId });
            if (res['code'] === 200) showMessage('User deleted successfully');
            else {
                await getCompany();
                showMessage(res['message'] || 'Failed to delete user');
            }
            dispatch(toggleModal({ open: '' }));
        } catch (error) {
            console.error('Error deleting user:', error);
            await getCompany();
            showMessage('Error deleting user');
        }
    };

    const handleStatusToggle = async (userId, currentStatus) => {
        const newStatus = currentStatus == 'active' ? 'inactive' : 'active';
        try {
            const originalStatus = company.externalMembers[userId]?.status;
            let updatedCompany = { ...company, externalMembers: { ...company.externalMembers, [userId]: { ...company.externalMembers[userId], status: newStatus } } };
            dispatch(setCompany(updatedCompany));

            const res = await postRequest(`/company/updateExternalUser/${company['_id']}`, { userId, action: 'updateStatus', status: newStatus });
            if (res['code'] === 200) showMessage(`User access ${newStatus} successfully`);
            else {
                let revertedCompany = { ...company, externalMembers: { ...company.externalMembers, [userId]: { ...company.externalMembers[userId], status: originalStatus } } };
                dispatch(setCompany(revertedCompany));
                showMessage(res['message'] || 'Failed to update status');
            }
        } catch (error) {
            console.error('Error updating status:', error);
            showMessage('Error updating status');
        }
    };

    const handleReactivateAccess = async (userId) => {
        try {
            const res = await postRequest(`/company/updateExternalUser/${company['_id']}`, { userId, action: 'updateExpiry' });
            if (res['code'] === 200) {
                showMessage('Access reactivated successfully');
                await getCompany();
            } else showMessage(res['message'] || 'Failed to reactivate access');
        } catch (error) {
            console.error('Error reactivating access:', error);
            showMessage('Error reactivating access');
        }
    };

    const handleSubmitShareForm = async () => {
        if (!externalUserDetails.firstName.trim()) return showMessage('Please enter a first name');
        if (!externalUserDetails.lastName.trim()) return showMessage('Please enter a last name');
        if (!externalUserDetails.email.trim()) return showMessage('Please enter a email address');
        if (Object.keys(reportPermissions).length == 0) return showMessage('Please select at least one report to share')
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(externalUserDetails.email)) return showMessage('Please enter a valid email address');

        try {
            let data = {
                firstName: externalUserDetails.firstName,
                lastName: externalUserDetails.lastName,
                email: externalUserDetails.email,
                reportPermissions,
                filePermissions
            };

            const res = await postRequest(`/company/shareWithExternalUser/${company['_id']}`, data);
            if (res['code'] == 200) {
                showMessage(res['message']);
                // Refresh company data to show new external user in table
                await getCompany();
                setExternalUserDetails({ firstName: '', lastName: '', email: '' });
                setFilePermissions([]);
                setReportPermissions({});
                dispatch(toggleModal({ open: '' }));
            }
            else showMessage(res['message'] || 'Failed to share diligence pack');
        } catch (error) {
            console.error('Error sharing diligence pack:', error);
            showMessage('An error occurred while sharing the diligence pack');
        }
    };


    return (
        <>
            <div className="w100 pb4rem">
                <div className="df jcc my2rem">
                    <h2 className="c15 fs1-5rem tac">Share Diligence Pack</h2>
                    <div class="df jcc pa r0 cp">
                        <Link to='#diligence-pack' onClick={() => setTab('diligence-pack')} class='df jcc px2rem py0-75rem br25px bg3 c1 fs1rem mr0-5rem'>
                            Show Diligence Pack
                        </Link>
                        <button onClick={() => dispatch(toggleModal({ open: 'shareExternalUser' }))} className="cp df aic jcc px2rem py0-75rem br25px bg4 c1 fs1rem">
                            <i class="fa fa-share-alt c1 mr0-5rem" />
                            Share with external user
                        </button>
                    </div>
                </div>

                {/* Table Container */}
                <div className="w100 oa share-diligence-table">
                    <table className="w100 table-advisor">
                        <thead>
                            <tr>
                                <th className="bg5 tac fw600 fs1-25rem py1rem px0-75rem">Name</th>
                                <th className="bg5 tac fw600 fs1-25rem py1rem px0-75rem">Firm</th>
                                <th className="bg5 tac fw600 fs1-25rem py1rem px0-75rem">Type</th>
                                <th className="bg5 tac fw600 fs1-25rem py1rem px0-75rem">Email Address</th>
                                <th className="bg5 tac fw600 fs1-25rem py1rem px0-75rem">Last Login</th>
                                <th className="bg5 tac fw600 fs1-25rem py1rem px0-75rem" style={{ minWidth: '250px' }}>Report Permissions</th>
                                {docsArray.length > 0 && <th className="bg5 tac fw600 fs1-25rem py1rem px0-75rem" style={{ minWidth: '250px' }}>File Permissions</th>}
                                <th className="bg5 tac fw600 fs1-25rem py1rem px0-75rem">Status</th>
                                <th className="bg5 tac fw600 fs1-25rem py1rem px0-75rem">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {allUsers.length === 0
                                ? <tr><td colSpan={docsArray.length > 0 ? 9 : 8} className="tac py2rem c4 fw600 fs1-25rem">No external users found!</td></tr>
                                : <> {allUsers.map((user) => (
                                    <tr key={user['id']}>
                                        <td className="tac py1rem px0-75rem">{user['name']}</td>
                                        <td className="tac py1rem px0-75rem">{user['firm']}</td>
                                        <td className="tac py1rem px0-75rem"><span className='fw500'>{user['type']}</span></td>
                                        <td className="tac py1rem px0-75rem"><a href={`mailto:${user['email']}`} className="c3 c4-hover">{user['email']}</a></td>
                                        <td className="tac py1rem px0-75rem">{user['lastLoginAt']}</td>
                                        <td className="tac py1rem px0-75rem">
                                            <FormControl size="small" sx={{ m: 1, minWidth: 200 }}>
                                                <Select
                                                    multiple
                                                    displayEmpty
                                                    value={(() => {
                                                        let perms = user?.reportPermissions || {};
                                                        return permissionLists.filter(p => perms[p] === true);
                                                    })()}
                                                    onChange={(e) => updatePermissions(user['id'], 'reportPermissions', e.target.value)}
                                                    renderValue={(selected) => {
                                                        if (selected.length === 0) return <em class='c14 fs0-875rem'>Select permissions</em>;
                                                        else return <span class='fs0-875rem'>{selected.length} report{selected.length > 1 ? 's' : ''} allocated</span>;
                                                    }}
                                                    MenuProps={{ PaperProps: { style: { maxHeight: 250, width: 300 }, }, }}>
                                                    {permissionLists.map((permission) => (
                                                        <MenuItem key={permission} value={permission}>
                                                            <Checkbox className='cp fs0-5rem c4 mr0-5rem'
                                                                checked={(() => {
                                                                    let perms = user?.reportPermissions || {};
                                                                    return perms[permission] === true;
                                                                })()} />
                                                            <ListItemText primary={permission} primaryTypographyProps={{ style: { fontSize: '14px', color: '#4a4a4a', textTransform: 'capitalize' } }} />
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </td>
                                        {docsArray.length > 0 &&
                                            <td className="tac py1rem px0-75rem">
                                                <FormControl size="small" sx={{ m: 1, minWidth: 200 }}>
                                                    <Select
                                                        multiple
                                                        displayEmpty
                                                        value={user?.filePermissions || []}
                                                        onChange={(e) => updatePermissions(user['id'], 'filePermissions', e.target.value)}
                                                        renderValue={(selected) => {
                                                            if (selected.length === 0) return <em class='c14 fs0-875rem'>Select files</em>;
                                                            else return <span class='fs0-875rem'>{selected.length} file{selected.length > 1 ? 's' : ''} allocated</span>;
                                                        }}
                                                        MenuProps={{ PaperProps: { style: { maxHeight: 250, width: 300, }, }, }}>
                                                        {docsArray.map((doc) => (
                                                            <Tooltip title={doc['name']} arrow placement="top">
                                                                <MenuItem className='df aic fs0-75rem c14 bw1px bss bo16 p0-5rem' key={doc['name']} value={doc['name']}>
                                                                    <Checkbox className='cp fs0-5rem c4 mr0-5rem' checked={user?.filePermissions?.includes(doc['name']) || false} />
                                                                    <ListItemText primary={doc['name']?.length > 20 ? doc['name']?.substring(0, 20) + '...' + doc['name']?.slice(-10) : doc['name']} primaryTypographyProps={{ style: { fontSize: '14px', color: '#4a4a4a' } }} />
                                                                </MenuItem>
                                                            </Tooltip>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </td>
                                        }
                                        <td className="tac py1rem px0-75rem">
                                            {['expired', 'revoked'].includes(user['status'])
                                                ? <Tooltip title={`Invite is ${user['status']}`} arrow><span><Switch checked={false} disabled={true} size="small" /></span></Tooltip>
                                                : <Switch checked={user['status'] == 'active'} onChange={() => handleStatusToggle(user['id'], user['status'])} size="small" color="success" />
                                            }
                                        </td>
                                        <td className="tac py1rem px0-75rem">
                                            <div className="df jcc aic">
                                                <button onClick={() => handleCopyShareLink(user['shareCode'])} className="cp p0-5rem br5px"><i class="fa fa-copy c7 c4-hover" data-tooltip='Copy Share Link' style={{ fontSize: '18px' }} /></button>
                                                <button onClick={() => handleResendInvite(user['id'], user['email'])} className="cp p0-5rem br5px"><i class="fa fa-repeat c7 c4-hover" data-tooltip='Resend Email' style={{ fontSize: '18px' }} /></button>
                                                <button onClick={() => handleReactivateAccess(user['id'])} className="cp p0-5rem br5px"><i class="fa fa-clock-o c7 c4-hover" data-tooltip='Reactivate Access' style={{ fontSize: '18px' }} /></button>
                                                <button onClick={async () => { await setSelectedUser(user), dispatch(toggleModal({ open: 'deleteExternalUser' })) }} className="cp p0-5rem br5px"><i class="fa fa-trash c23 c9-hover" data-tooltip='Delete User' style={{ fontSize: '18px' }} /></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </>}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* External User Share Modal */}
            <Modal open={modal['open'] == 'shareExternalUser'} onClose={() => {
                setFilePermissions([]);
                setReportPermissions({});
                setExternalUserDetails({ firstName: '', lastName: '', email: '' });
                dispatch(toggleModal({ open: '' }));
            }} className="backdrop td500 zi4 pf t0 l0 w100 h100vh df jcc aic">
                <div className="poppins-font-style mah100 bg1 shadow w100 py2rem px3rem df fdc m1rem br10px pr" style={{ maxWidth: '750px' }}>
                    <h2 className="c15 fs1-5rem fw600 mb0-5rem">Share with External User</h2>
                    <p className="c14 fs1rem mb1rem">Share this Diligence Pack with someone outside of Diligentsia.</p>

                    <div class='df fdc oa h70vh px2rem'>
                        {/* User Details Form */}
                        <div className="mb1rem">
                            <h3 className="c15 fs1-25rem fw600 mb0-5rem">Recipient Details</h3>
                            <div className="mb1rem">
                                <label className="db c14 mb0-5rem fs0-875rem fw500">First Name <span className="c3">*</span></label>
                                <TextField
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    placeholder="Enter first name"
                                    value={externalUserDetails['firstName']}
                                    onChange={(e) => setExternalUserDetails({ ...externalUserDetails, firstName: e.target.value })}
                                />
                            </div>
                            <div className="mb1rem">
                                <label className="db c14 mb0-5rem fs0-875rem fw500">Last Name <span className="c3">*</span></label>
                                <TextField
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    placeholder="Enter last name"
                                    value={externalUserDetails['lastName']}
                                    onChange={(e) => setExternalUserDetails({ ...externalUserDetails, lastName: e.target.value })}
                                />
                            </div>
                            <div className="mb1rem">
                                <label className="db c14 mb0-5rem fs0-875rem fw500">Email Address <span className="c3">*</span></label>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="email"
                                    variant="outlined"
                                    value={externalUserDetails.email}
                                    placeholder="Enter email address"
                                    onChange={(e) => setExternalUserDetails({ ...externalUserDetails, email: e.target.value })}
                                />
                            </div>
                        </div>

                        {/* Reports Permission */}
                        <div className="mb2rem">
                            <h3 className="c15 fs1-25rem fw600 mb1rem">Select Reports to Share</h3>
                            {/* Select All Checkbox for Reports */}
                            <div className="df aic pb1rem">
                                <label className="df fs0-75rem">
                                    <span className="checkbox w20px h20px pr">
                                        <input
                                            type="checkbox"
                                            id="selectAllReports"
                                            checked={Object.keys(reportPermissions).filter(key => reportPermissions[key]).length === permissionLists.length}
                                            onChange={(e) => {
                                                const newPermissions = {};
                                                if (e.target.checked) permissionLists.forEach(permission => newPermissions[permission] = true);
                                                setReportPermissions(newPermissions);
                                            }}
                                            className={`cp bg1 bo4 bw1px bss td250 w20px h20px br5px`}
                                        />
                                    </span>
                                    <span className={`w100 c15 fs0-875rem lh3 ml0-5rem usn mr2rem fw600`}>Select All Reports</span>
                                </label>
                            </div>
                            <div className="section-list section-list">
                                {permissionLists.map((permission, idx) => {
                                    return <div key={idx} className="df aic pb1rem">
                                        <label key={idx} for={permission + idx} className="df fs0-75rem">
                                            <span className="checkbox w20px h20px pr">
                                                <input
                                                    type="checkbox"
                                                    id={permission + idx}
                                                    data-option={permission}
                                                    checked={reportPermissions[permission] || false}
                                                    onChange={(e) => setReportPermissions({ ...reportPermissions, [permission]: e.target.checked })}
                                                    className={`cp bg1 bo4 bw1px bss td250 w20px h20px br5px`}
                                                />
                                            </span>
                                            <span className={`w100 c15 fs0-75rem lh3 ml0-5rem usn mr2rem fw500`}>{permission}</span>
                                        </label>
                                    </div>
                                })}
                            </div>
                        </div>

                        {/* Files Permission */}
                        {docsArray.length > 0 &&
                            <div className="mb2rem">
                                <h3 className="c15 fs1-25rem fw600 mb1rem">Select Files to Share</h3>
                                {/* Select All Checkbox for Files */}
                                <div className="df aic pb1rem">
                                    <label className="df fs0-75rem">
                                        <span className="checkbox w20px h20px pr">
                                            <input
                                                type="checkbox"
                                                id="selectAllFiles"
                                                checked={docsArray.length > 0 && filePermissions.length === docsArray.length}
                                                onChange={(e) => {
                                                    if (e.target.checked) setFilePermissions(docsArray.map(doc => doc.name));
                                                    else setFilePermissions([]);
                                                }}
                                                className={`cp bg1 bo4 bw1px bss td250 w20px h20px br5px`}
                                            />
                                        </span>
                                        <span className={`w100 c15 fs0-875rem lh3 ml0-5rem usn mr2rem fw600`}>Select All Files</span>
                                    </label>
                                </div>
                                <div className="section-list section-list">
                                    {docsArray.map((doc, idx) => {
                                        return <div key={idx} className="df aic pb1rem">
                                            <label key={idx} for={doc?.['name'] + idx} className="df fs0-75rem">
                                                <span className="checkbox w20px h20px pr">
                                                    <input
                                                        type="checkbox"
                                                        id={doc?.['name'] + idx}
                                                        data-option={doc?.['name']}
                                                        checked={filePermissions.includes(doc['name'])}
                                                        onChange={(e) => {
                                                            if (e.target.checked) setFilePermissions([...filePermissions, doc['name']]);
                                                            else setFilePermissions(filePermissions.filter(file => file !== doc['name']));
                                                        }}
                                                        className={`cp bg1 bo4 bw1px bss td250 w20px h20px br5px`}
                                                    />
                                                </span>
                                                <span className={`w100 c15 fs0-75rem lh3 ml0-5rem usn mr2rem fw500`}>{doc['name']?.length > 70 ? doc['name']?.substring(0, 40) + '...' + doc['name']?.slice(-20) : doc['name']}</span>
                                            </label>
                                        </div>
                                    })}
                                </div>
                            </div>
                        }
                    </div>
                    <div className="df jcfe aic mt1rem">
                        <button onClick={() => {
                            setFilePermissions([]);
                            setReportPermissions({});
                            setExternalUserDetails({ firstName: '', lastName: '', email: '' });
                            dispatch(toggleModal({ open: '' }));
                        }} className="cp mr1rem px2rem py0-75rem br25px bss bw1px bo4 bg1 c4 fs1rem">Cancel</button>
                        <button onClick={handleSubmitShareForm} className="cp px2rem py0-75rem br25px bg4 c1 fs1rem ">Submit</button>
                    </div>
                </div>
            </Modal>

            {/* Archive Lead Popup */}
            <ModalWrapper modalType={'deleteExternalUser'}>
                <div className='df'>
                    <img className='w20' src={require(`../../../../images/logo.svg`)} alt='TODO' />
                </div>
                <div className="row fdc tac my2rem">
                    <h1 className="c15 fs2rem fw600 tac ttu mb1rem">Delete External User</h1>
                    <span className="c15">Are you sure you want to delete <b>{selectedUser['name']}</b>?</span>
                </div>
                <div className="df aic jcsa mt2rem">
                    <Button text={"Cancel"} onClick={() => dispatch(toggleModal({ open: '' }))} customClassName="c1 ml1rem" />
                    <Button text={"Submit"} btnType="warning" onClick={() => handleDeleteUser(selectedUser['id'])} />
                </div>
            </ModalWrapper>

        </>
    );
}